import type {
  RouteLocationNormalized,
  RouteLocationRaw,
  RouteRecordNormalized,
  Router,
} from "vue-router";

import type { RouteMeta } from "../types/types";

import { tokenManager } from "@/shared/api/tokenManager";
import { hasPermissions, hasRoles } from "@/shared/permissions";
import { useUserStore } from "@/shared/stores/useUserStore";

// ============================================================================
// Router Instance Management
// ============================================================================

let routerInstance: Router | null = null;

export const setRouterForGuards = (router: Router): void => {
  routerInstance = router;
};

// ============================================================================
// Route Finding Utilities
// ============================================================================

const EXCLUDED_ROUTES = new Set(["Root", "NotFound", "Forbidden", "Login", "ForgotPassword"]);
const EXCLUDED_PATHS = new Set(["/", "/403", "/404", "/auth", "/forgot-password"]);

const findFirstAccessibleRoute = (routes: RouteRecordNormalized[]): RouteLocationRaw | null => {
  const candidates: Array<{ route: RouteRecordNormalized, meta: RouteMeta }> = [];

  for (const route of routes) {
    const meta = route.meta as RouteMeta;
    const routeName = String(route.name ?? "");

    if (
      meta.requiresAuth === false
      || !route.name
      || meta.showInMenu === false
      || route.redirect
      || route.path.includes("*")
      || EXCLUDED_ROUTES.has(routeName)
      || EXCLUDED_PATHS.has(route.path)
    ) {
      continue;
    }

    // Route must pass both role and permission checks
    if (hasRoles(meta.roles) && hasPermissions(meta.permissions)) {
      candidates.push({ route, meta });
    }
  }

  if (candidates.length === 0) return null;

  candidates.sort((a, b) => {
    const aInMenu = a.meta.showInMenu === true;
    const bInMenu = b.meta.showInMenu === true;
    if (aInMenu !== bInMenu) return aInMenu ? -1 : 1;

    const aOrder = a.meta.menuOrder ?? 999;
    const bOrder = b.meta.menuOrder ?? 999;
    if (aOrder !== bOrder) return aOrder - bOrder;

    const aDepth = a.route.path.split("/").filter(Boolean).length;
    const bDepth = b.route.path.split("/").filter(Boolean).length;
    return aDepth - bDepth;
  });

  return { name: candidates[0].route.name as string };
};

// ============================================================================
// Auth Initialization Helper
// ============================================================================

const ensureAuthInitialized = async (
  userStore: ReturnType<typeof useUserStore>,
): Promise<boolean> => {
  if (userStore.isInitialized) return true;

  const token = tokenManager.getAccessToken();
  if (!token) return false;

  try {
    await userStore.initialize();
    return true;
  }
  catch (error) {
    console.error("[Auth Guard] Initialization failed:", error);
    tokenManager.clearTokens();
    return false;
  }
};

// ============================================================================
// Navigation Guards
// ============================================================================

export const authGuard = async (
  to: RouteLocationNormalized,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _from: RouteLocationNormalized,
): Promise<RouteLocationRaw | boolean | undefined> => {
  const meta = to.meta as RouteMeta;
  const userStore = useUserStore();
  const requiresAuth = meta.requiresAuth !== false;

  if (!requiresAuth) return true;

  await ensureAuthInitialized(userStore);

  if (!userStore.isAuthenticated) {
    const loginPath = meta.authRedirect || "/auth";
    if (to.path === loginPath) return true;
    return { path: loginPath, replace: true };
  }

  // Root redirect — find the first route the user can access
  if (meta.isRootRedirect && routerInstance) {
    const firstAccessible = findFirstAccessibleRoute(routerInstance.getRoutes());
    if (firstAccessible) return Object.assign({}, firstAccessible, { replace: true });
    return { path: "/403", replace: true };
  }

  // Role gate — checked before permissions (hard gate, ADMIN bypass does not apply)
  if (!hasRoles(meta.roles)) {
    return { path: "/403", replace: true };
  }

  // Permission gate — ADMIN role bypasses this check
  if (!hasPermissions(meta.permissions)) {
    return { path: "/403", replace: true };
  }

  return true;
};

export const guestGuard = async (
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _to: RouteLocationNormalized,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _from: RouteLocationNormalized,
): Promise<RouteLocationRaw | boolean | undefined> => {
  const userStore = useUserStore();

  await ensureAuthInitialized(userStore);

  if (userStore.isAuthenticated) {
    return { path: "/", replace: true };
  }

  return true;
};
